<template>
  <div
    :class="`data-table report-table ${addPaddingToTable ? 'summaries-expanded' : 'px-0'}`"
    @mousemove="keepDragging($event)"
    @mouseup="stopDragging($event)"
  >
    <v-col
      data-name="table-groups-container"
      :class="addPaddingToTable ? 'pt-4' : 'pt-0'"
    >
      <v-row
        v-for="(group, groupIndex) of tableGroups"
        :key="groupIndex"
        :class="`table-group px-0 rounded-borders ${clearColor}${ addPaddingToTable ? ' mt-0' : ''}`"
      >
        <v-col
          v-if="!group.isPartOfGroup || group.items.length > 0"
          :class="`${group.isPartOfGroup ? 'rounded-borders cursor-pointer px-6 ' : 'px-4'}`"
        >
          <v-row
            class="row--dense align-center"
            @click="toggleRows(group.groupName)"
          >
            <v-col
              v-if="group.groupName"
              class="col-auto align-center py-4 overline"
            >
              <v-row>
                <span class="font-weight-bold primary--text">
                  {{ group.labels[0] }}
                </span>

                <span
                  class="font-weight-regular pl-3"
                >
                  {{ group.labels.slice(1).join(' :: ') }}
                </span>
              </v-row>
            </v-col>

            <v-col
              v-else
              class="col-auto align-center px-5 pb-0 pt-1 caption"
            >
              <v-row>
                <span class="pr-1">Viewing</span>

                <span
                  class="font-weight-bold darkblue--text"
                >{{ selectedReport.reportLabel }}</span>

                <span
                  v-if="selectedMetric && selectedMetric.reportLabel"
                  class="px-1"
                >and</span>

                <span
                  v-if="selectedMetric && selectedMetric.reportLabel"
                  class="font-weight-bold darkblue--text"
                >{{ selectedMetric.reportLabel }}</span>
              </v-row>
            </v-col>

            <v-col
              :class="`align-center coolgray--text text-right ${group.isPartOfGroup ? '' : ''}`"
            >
              <v-row class="justify-end">
                <v-col
                  :class="`coolgray--text justify-start py-0 col-auto`"
                  style="min-width: fit-content"
                  title="Results"
                >
                  <span class="caption font-weight-bold">
                    {{ formatNumber(group.items.length) }}
                  </span>

                  <span class="caption align-center font-weight-regular pl-1">
                    {{ group.items.length === 1 ? 'Result' : 'Results' }}
                  </span>
                </v-col>

                <v-col
                  v-for="(summary, summaryIndex) in group.summaries"
                  :key="summaryIndex"
                  :class="`coolgray--text justify-start py-0 my-0 col-auto`"
                  :title="summary.title"
                  :style="`min-width: ${summary.widthPx};`"
                >
                  <span class="font-weight-bold caption">
                    {{ summary.value }}
                  </span>

                  <span class="caption align-center font-weight-regular pl-1">
                    {{ summary.title }}
                  </span>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <v-divider />
          </v-row>

          <v-row>
            <v-data-table
              v-if="(!group.isPartOfGroup && tableGroups.length === 1) || showRows.indexOf(group.groupName) !== -1"
              cell-class="font-weight-regular"
              dense
              :custom-sort="setTableSorting"
              :footer-props="tableFooterProps"
              :headers="visibleColumns"
              hide-default-header
              hide-default-footer
              :items="group.items || []"
              :items-per-page="syncItemsPerPage"
              :loading="isLoadingDataTable(group)"
              multi-sort
              :page.sync="syncPage"
              :sort-by.sync="sortedByKeys"
              :sort-desc.sync="sortedByDirections"
              :class="`mx-0 cursor-context-menu scroller tbody ${group.isPartOfGroup ? 'is-part-of-group' : ''}`"
              @page-count="syncPageCount = $event"
            >
              <template v-slot:header="{ props: { headers } }">
                <thead
                  style="position: sticky; top: 0px;"
                  @contextmenu="toggleColumnMenu($event)"
                  @dblclick="toggleColumnMenu($event)"
                >
                  <tr>
                    <th
                      v-for="(header, headerIndex) in headers"
                      :key="headerIndex"
                      :class="`coolgray--text font-weight-semibold ${header.classes}`"
                      :style="header.styles"
                      :title="header.tooltip || header.text"
                    >
                      <v-row
                        :style="header.widthStyles"
                        nowrap
                      >
                        <v-col
                          :class="`col-auto d-flex align-center justify-${header.align} py-0`"
                        >
                          <div
                            :class="`text-truncate ${header.sortClassText}`"
                            :style="`max-width: ${header.width - 90}px;`"
                          >
                            {{ header.text }}
                          </div>
                          <v-chip
                            :class="`cursor-pointer transparent ${header.sortClassIndicator}`"
                            label
                            outlined
                            overlap
                            top
                            x-small
                            @click="updateSorting(header)"
                          >
                            <v-row>
                              <v-icon
                                small
                              >
                                {{ header.sortIcon }}
                              </v-icon>
                              <div class="text-smaller">
                                {{ (header.sortIndex !== -1 && header.sortIndex) || '–' }}
                              </div>
                            </v-row>
                          </v-chip>
                        </v-col>

                        <column-resizer
                          :column-index="headerIndex"
                          :column-width="header.width"
                        />
                      </v-row>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:item="{ headers, item, index }">
                <tr
                  :class="getRowBackground(contextMenuItemIndex, index)"
                  @contextmenu="toggleContextMenu($event, item, index)"
                  @dblclick="toggleContextMenu($event, item, index)"
                  @click="resetContextMenu(null)"
                >
                  <td
                    v-for="(header, hIndex) in headers"
                    :key="hIndex"
                    :class="header.classes"
                    :style="header.styles"
                  >
                    <v-row
                      :style="header.widthStyles"
                    >
                      <table-column
                        :header="header"
                        :item="item"
                      />
                    </v-row>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-row>

          <v-menu
            v-model="contextMenu"
            absolute
            offset-y
            :position-x="contextMenuX"
            :position-y="contextMenuY"
            transition="scale-transition"
            width="300px"
          >
            <v-card>
              <v-card-actions>
                <v-list dense>
                  <v-list-item
                    v-for="(action, actionIndex) in actions"
                    :key="actionIndex"
                    @click="fireActionEvent(action, contextMenuItem)"
                  >
                    <v-list-item-content class="body-2">
                      {{ action.name }}
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-actions>
            </v-card>
          </v-menu>

          <v-menu
            v-model="columnMenu"
            absolute
            max-height="300"
            min-width="300"
            offset-y
            :close-on-content-click="false"
            :position-x="columnMenuX"
            :position-y="columnMenuY"
            bottom
            transition="scale-transition"
            width="300px"
          >
            <column-picker
              :current-required-columns="currentRequiredColumns"
              :report-name="baseReportName"
              :table-columns="tableColumns"
            />
          </v-menu>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<script>
import _ from 'underscore'
import { createNamespacedHelpers } from 'vuex'

import { fireanalytics } from 'src/store/firebase'

import dates from 'src/utils/dates'
import format from 'src/utils/format'
import sort from 'src/utils/sort'

import ColumnPicker from './ColumnPicker'
import ColumnResizer from './ColumnResizer'
import TableColumn from './TableColumn'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters, mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapState: mapReportState } = createNamespacedHelpers('report')
const { mapActions, mapGetters, mapState } = createNamespacedHelpers('tools')

const { direction: SortDirection, label: SortLabel } = sort

export default {
  name: 'DataTable',
  components: {
    // Dynamic import caused a split-second lag.
    ColumnPicker,
    ColumnResizer,
    TableColumn,
  },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    dataGroups: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      columnMenu: false,
      columnMenuX: 0,
      columnMenuY: 0,
      contextMenu: false,
      contextMenuItem: null,
      contextMenuItemIndex: null,
      contextMenuX: 0,
      contextMenuY: 0,
      format,
      observer: null,
      SortDirection,
      tableOptions: {},
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    ...mapLayoutGetters(['clearColor']),
    ...mapLayoutState({
      $isLayoutFullScreen: (state) => state.isLayoutFullScreen,
    }),
    ...mapReportState({
      $selectedReport: (state) => state.selectedReport,
    }),
    ...mapGetters([
      'columnsByKey',
      'currentColumns',
      'currentItems',
      'currentGroupByProperties',
      'currentGroups',
      'currentMetric',
      'currentlySorting',
      'currentNumberOfAppliedFilters',
      'currentSorted',
      'currentSorting',
      'currentSummary',
      'currentViewId',
      'frozenColumns',
      'isProgrammaticRoute',
      'keyField',
      'reportColumns',
      'sizeField',
      'sortByDirections',
      'sortByKeys',
      'storedItems',
      'tableColumns',
      'visibleColumns',
    ]),
    ...mapState({
      applyingFilter: (state) => state.applyingFilter,
      dragging: (state) => state.dragging,
      hasClickedToggler: (state) => state.hasClickedToggler,
      isTogglingTables: (state) => state.isTogglingTables,
      itemsPerPage: (state) => state.itemsPerPage,
      itemsPerPageOptions: (state) => state.itemsPerPageOptions,
      page: (state) => state.page,
      pageCount: (state) => state.pageCount,
      programmaticRoutes: (state) => state.programmaticRoutes,
      associationId: (state) => state.associationId,
      baseReportName: (state) => state.baseReportName,
      resettingFilter: (state) => state.resettingFilter,
      showRows: (state) => state.showRows,
      subId: (state) => state.subId,

      requiredColumns: (state) => state.requiredColumns,
      sortClassIndicatorMap: (state) => state.sortClassIndicatorMap,
      sortClassTextMap: (state) => state.sortClassTextMap,
      sortIcons: (state) => state.sortIcons,
      sortingByKeys: (state) => state.sortingByKeys,
      sortingByDirections: (state) => state.sortingByDirections,
      tableSortMap: (state) => state.tableSortMap,
    }),

    addPaddingToTable() {
      return !this.$isLayoutFullScreen
    },

    selectedReport() {
      return this.$selectedReport || {}
    },
    selectedMetric() {
      return this.currentMetric || {}
    },

    syncPage: {
      get() {
        return this.page
      },
      set(value) {
        this.$store.commit('tools/page', value)
      },
    },
    syncPageCount: {
      get() {
        return this.pageCount
      },
      set(value) {
        this.$store.commit('tools/pageCount', value)
      },
    },
    syncItemsPerPage: {
      get() {
        return this.itemsPerPage
      },
      set(value) {
        this.$store.commit('tools/itemsPerPage', value)
      },
    },

    sortedByDirections: {
      get() {
        return this.sortByDirections
      },
      set(newValue) {
        this.$store.commit('tools/sortingByDirections', newValue)
      },
    },
    sortedByKeys: {
      get() {
        return this.sortByKeys
      },
      set(newValue) {
        this.$store.commit('tools/sortingByKeys', newValue)
      },
    },

    currentRequiredColumns() {
      return this.requiredColumns[this.baseReportName] || []
    },
    isReady() {
      return !!this.currentViewId
    },
    isReadyAndHasResults() {
      const { currentGroups, isReady } = this
      return isReady && !!currentGroups?.length
    },
    isReadyAndNoGroups() {
      const { currentGroups, isReady } = this

      return isReady && !currentGroups?.length
    },
    allGroupsAreEmpty() {
      const { currentGroups } = this
      const foundItems = Array.isArray(currentGroups) && currentGroups.find((x) => x.items.length > 0)

      return !foundItems
    },
    isReadyAndNoData() {
      const { allGroupsAreEmpty, isReadyAndNoGroups } = this

      return isReadyAndNoGroups || allGroupsAreEmpty
    },
    tableGroups() {
      const { allGroupsAreEmpty, isReady, isReadyAndNoData } = this
      const getEmptyTableGroups = () => {
        const emptyGroup = {
          groupName: 'No Data',
          labels: ['No Data'],
          isPartOfGroup: false,
          items: [],
          summaries: [],
          totalCount: 0,
        }
        const hasNoData = !isReady || isReadyAndNoData || allGroupsAreEmpty

        return hasNoData && [emptyGroup]
      }
      const getTableGroups = () => this.dataGroups || this.currentGroups

      return getEmptyTableGroups() || getTableGroups()
    },
    tableFooterProps() {
      return {
        absolute: true,
        'items-per-page-options': this.itemsPerPageOptions,
      }
    },
    topPadding() {
      return this.currentGroupByProperties.length ? 'mt-1' : 'mt-0'
    },
  },
  methods: {
    ...mapActions(['keepDragging', 'startDragging', 'stopDragging', 'toggleRows']),
    isLoadingDataTable(group) {
      const { applyingFilter, isReady, resettingFilter } = this
      return !isReady || (group.items.length > 0 && (applyingFilter || resettingFilter))
    },
    toggleColumnMenu(event) {
      event.preventDefault()

      const newValue = !this.columnMenu

      if (newValue) {
        this.columnMenuX = event.clientX
        this.columnMenuY = event.clientY
        this.columnMenu = newValue
      } else {
        this.columnMenu = newValue
      }
    },
    resetContextMenu(newValue) {
      this.contextMenu = newValue
      this.contextMenuItemIndex = null
      this.contextMenuItem = null
    },
    toggleContextMenu(event, item, itemIndex) {
      event.preventDefault()

      const newValue = !this.contextMenu

      if (newValue) {
        this.contextMenuItem = item
        this.contextMenuItemIndex = itemIndex
        this.contextMenuX = event.clientX
        this.contextMenuY = event.clientY
        this.contextMenu = newValue
      } else {
        this.resetContextMenu(newValue)
      }
    },
    getRowBackground(contextMenuItemIndex, rowIndex) {
      const shadedMap = {
        light: 'nearwhite',
        dark: 'nearblack',
      }
      const clearMap = {
        light: 'white',
        dark: 'black',
      }
      const opaqueMap = {
        light: 'coolbeige',
        dark: 'blueblack',
      }

      const { inDarkMode } = this
      const mapKey = inDarkMode ? 'dark' : 'light'

      const getAlternatingRowColor = () => (rowIndex % 2 === 0 ? clearMap[mapKey] : shadedMap[mapKey])
      const getSelectedColor = () => contextMenuItemIndex === rowIndex && opaqueMap[mapKey]

      return getSelectedColor() || getAlternatingRowColor()
    },
    fireActionEvent(action, item) {
      this.$store.dispatch(action.event, item)
    },
    haveDataButNoResultsInGroup(group) {
      const { allGroupsAreEmpty, isReadyAndHasResults } = this

      if (allGroupsAreEmpty) return false

      return isReadyAndHasResults && group.items.length === 0
    },
    getClassNames(value, column) {
      const { formatAs = 'text' } = column
      const isNonTextType = ['boolean', 'number', 'percentage'].indexOf(formatAs) !== -1
      const formatClass = `column-${isNonTextType ? 'narrow' : formatAs}`
      const xAxisPadding = 'pl-0 pr-3'
      // const isFirstColumn = columnIndex === 0
      // const xAxisPadding = isFirstColumn ? 'px-3' : 'pl-0 pr-3'

      return `${xAxisPadding} ${formatClass} ${column.classes}`
    },
    formatNumber(value) {
      return format.number(value)
    },
    sortItems({ columnKey, columnDirection, columnFormat, items }) {
      const sortDsc = columnDirection === this.tableSortMap.DSC
      const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
      const isDateColumn = columnFormat === 'date'

      const valueGetter = (item) => {
        const getValue = () => {
          const value = !isDateColumn && item.normalized ? item.normalized[columnKey] : item[columnKey]
          const sortArrayValues = () => (sortDsc ? value.sort().reverse() : value.sort())
          return Array.isArray(value) ? sortArrayValues()[0] : value?.value || value
        }
        const getDate = (value) => dates.getUnixDate(value)
        return isDateColumn ? getDate(getValue()) : getValue()
      }

      return sorter(items, valueGetter)
    },
    addSortingOption(optionIndex, header) {
      const { ASC } = this.tableSortMap
      const addOption = () => {
        this.sortedByKeys.splice(this.sortedByKeys.length, 0, header.key)
        this.sortedByDirections.splice(this.sortedByDirections.length, 0, ASC)

        header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = this.sortedByKeys.length

        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Add Sort Option',
          label: `Add Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        return true
      }

      return (optionIndex === -1 && addOption()) || false
    },
    deleteSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const removeOption = () => {
        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Remove Sort Option',
          label: `Remove Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        this.sortedByDirections.splice(optionIndex, 1)
        this.sortedByKeys.splice(optionIndex, 1)

        this.sortedByKeys.forEach((key, sortIndex) => {
          const foundHeader = this.columnsByKey[key]
          foundHeader.sortIndex = sortIndex + 1
        })

        // @TODO Avoid issue w/ sortClassIndicator's text color pre- vs post-click by abstracting the values.
        header.sortClassIndicator = `midgray--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = -1

        return true
      }
      return (this.sortedByDirections[optionIndex] === DSC && removeOption()) || false
    },
    updateSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const currentDirection = this.sortedByDirections[optionIndex]
      const newDirection = currentDirection === DSC ? ASC : DSC
      const newSortIndex = optionIndex + 1

      fireanalytics.event('goal_completion', {
        action: 'click',
        category: 'Sort',
        description: 'Update Sort Option',
        label: `Update Sort Option "${header.columnName}" (${header.key}) from Direction "${SortLabel[header.sortDsc]}" to Direction "${newDirection}" at Index ${header.sortIndex}`,
      })

      header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
      header.sortDsc = newDirection
      header.sortIcon = this.sortIcons[newDirection]
      header.sortIndex = newSortIndex

      this.sortedByDirections.splice(optionIndex, 1, newDirection)

      return true
    },
    updateSorting(header) {
      const optionIndex = this.sortedByKeys.findIndex((key) => key === header.key)

      return this.addSortingOption(optionIndex, header)
          || this.deleteSortingOption(optionIndex, header)
          || this.updateSortingOption(optionIndex, header)
    },
    setTableSorting(unsortedItems) {
      const { columnsByKey } = this
      const sortItems = (items, columnKey, index) => this.sortItems({
        columnDirection: this.sortedByDirections[index],
        columnFormat: columnsByKey[columnKey]?.formatAs,
        columnKey,
        items,
      })

      return this.sortedByKeys.reduceRight(sortItems, unsortedItems)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "src/styles/tables";

#header-thead {
  position: sticky;
  top: 0;
}

#header-tbody {
  position: absolute;
  top: 0;

  overflow: scroll;
  width: max-content;

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
#header-tbody::-webkit-scrollbar {
  display: none;
}
</style>
